import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Pencil, RefreshCw, Repeat, MapPin } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const emptyForm = {
  pickup: '', dropoff: '', rideTypeId: 'standard',
  daysOfWeek: [0, 1, 2, 3, 4], time: '07:00', label: '', enabled: true,
};

const RecurringRides = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [rideTypes, setRideTypes] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const api = (await import('../utils/api')).default;
      const [r, t] = await Promise.all([
        api.get('/recurring-rides'),
        api.get('/ride-types'),
      ]);
      setItems(r.data.items || []);
      setRideTypes(t.data.rideTypes || []);
    } catch {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      pickup: r.pickup, dropoff: r.dropoff, rideTypeId: r.rideTypeId,
      daysOfWeek: r.daysOfWeek || [], time: r.time, label: r.label || '', enabled: r.enabled,
    });
    setShowDialog(true);
  };

  const toggleDay = (i) => {
    setForm(f => ({
      ...f, daysOfWeek: f.daysOfWeek.includes(i) ? f.daysOfWeek.filter(d => d !== i) : [...f.daysOfWeek, i].sort(),
    }));
  };

  const handleSave = async () => {
    if (!form.pickup.trim() || !form.dropoff.trim()) { toast({ title: 'Pickup and dropoff required', variant: 'destructive' }); return; }
    if (form.daysOfWeek.length === 0) { toast({ title: 'Select at least one day', variant: 'destructive' }); return; }
    const payload = {
      pickup: form.pickup.trim(), dropoff: form.dropoff.trim(),
      rideTypeId: form.rideTypeId, daysOfWeek: form.daysOfWeek,
      time: form.time, label: form.label.trim() || null, enabled: form.enabled,
    };
    try {
      const api = (await import('../utils/api')).default;
      if (editing) {
        await api.put(`/recurring-rides/${editing}`, payload);
        toast({ title: 'Updated' });
      } else {
        await api.post('/recurring-rides', payload);
        toast({ title: 'Recurring ride created' });
      }
      setShowDialog(false); fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Save failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recurring ride?')) return;
    try {
      const api = (await import('../utils/api')).default;
      await api.delete(`/recurring-rides/${id}`);
      toast({ title: 'Deleted' });
      fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const toggleEnabled = async (r) => {
    try {
      const api = (await import('../utils/api')).default;
      await api.put(`/recurring-rides/${r.id}`, { enabled: !r.enabled });
      fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-8">
      <div className="max-w-4xl mx-auto px-4 space-y-6" data-testid="recurring-rides-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Repeat className="h-6 w-6 text-pink-600" /> Recurring Rides</h1>
            <p className="text-sm text-gray-500">Save commute templates — we'll auto-book a ride at your scheduled time on the selected days.</p>
          </div>
          <Button onClick={openNew} className="bg-pink-600 hover:bg-pink-700" data-testid="add-recurring-btn">
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        </div>

        {loading && <p className="text-gray-500">Loading...</p>}

        {!loading && items.length === 0 && (
          <Card className="p-8 text-center text-gray-500">
            <RefreshCw className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No recurring rides yet. Set up your daily commute and skip booking each morning.</p>
          </Card>
        )}

        <div className="space-y-3">
          {items.map(r => (
            <Card key={r.id} className="p-4">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{r.label || 'Commute'}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 font-bold">{r.time}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {r.enabled ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-pink-600" />{r.pickup} → {r.dropoff}
                  </p>
                  <div className="flex gap-1 mt-2">
                    {DAYS.map((d, i) => (
                      <span key={d} className={`text-[10px] px-1.5 py-0.5 rounded ${(r.daysOfWeek || []).includes(i) ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-400'}`}>{d}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={r.enabled} onCheckedChange={() => toggleEnabled(r)} />
                  <Button size="sm" variant="outline" onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-md" data-testid="recurring-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Recurring Ride</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Label (optional)</Label>
                <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Morning commute" />
              </div>
              <div>
                <Label>Pickup</Label>
                <Input value={form.pickup} onChange={e => setForm({ ...form, pickup: e.target.value })} placeholder="Kasama Town Centre" data-testid="recurring-pickup" />
              </div>
              <div>
                <Label>Dropoff</Label>
                <Input value={form.dropoff} onChange={e => setForm({ ...form, dropoff: e.target.value })} placeholder="Lusaka" data-testid="recurring-dropoff" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Time</Label>
                  <Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} />
                </div>
                <div>
                  <Label>Ride type</Label>
                  <select value={form.rideTypeId} onChange={e => setForm({ ...form, rideTypeId: e.target.value })} className="w-full border rounded-md px-3 py-2 text-sm">
                    {rideTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Days of week</Label>
                <div className="flex gap-1 mt-1">
                  {DAYS.map((d, i) => (
                    <button
                      key={d} type="button" onClick={() => toggleDay(i)}
                      className={`flex-1 px-2 py-2 rounded text-xs font-medium border ${form.daysOfWeek.includes(i) ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    >{d}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
                <Label className="!mb-0">Enabled</Label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={handleSave} data-testid="recurring-save-btn">Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default RecurringRides;
